import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { IconComponent } from '../icon/icon.component';
import { Icon } from '../icon/icon.enum';

@Component({
  selector: 'app-button',
  imports: [IconComponent, CommonModule, RouterLink],
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
})
export class ButtonComponent {
  /** Id for button */
  @Input({ required: true }) buttonId!: string;
  /** Title for button */
  @Input() buttonTitle = '';
  /** Element type */
  @Input() elementType: 'button' | 'anchor' = 'button';
  /** Href for anchor tag */
  @Input() routerLink?: string;
  /** Fragment link for anchor tag */
  @Input() fragmentLink?: string;
  /** If button should have icons */
  @Input() icon?: {
    /** Icon placement inside button. Left | Right */
    placement?: 'left' | 'right';
    /** Icontype */
    iconType: Icon;
    /** Icon color*/
    iconColor?: string;
    /** Rotation of icon in degrees */
    iconRotation?: number;
    /** Icon height */
    iconHeight?: number;
    /** Icon width */
    iconWidth?: number;
    /** Custom CSS classes for the icon */
    iconClass?: string;
  };
  /** Form id for binding */
  @Input() form?: string;
  /** Button text */
  @Input() buttonText?: string;
  /** Custom styling class for button */
  @Input() customClasses?: string | string[] | { [key: string]: boolean };
  /** Custom styling for button host */
  @Input() customHostClasses?: string | string[] | { [key: string]: boolean };
  /** Gap between icon and text (Tailwind gap classes like 'gap-1', 'gap-2', etc.) */
  @Input() flexGap?: string;
  /** Horizontal alignment (Tailwind justify classes like 'justify-center', 'justify-start', etc.) */
  @Input() justifyContent?: string = 'justify-start';
  /** If button should be disabled or not */
  @Input() disabled?: boolean = false;
  /** If the button should be hidden or not */
  @Input() hidden?: boolean = false;
  /** Aria-Describe-By */
  @Input() describedBy?: string;
  /** TODO define how to use it */
  @Input() description?: string;
  /** Aria-label */
  @Input() buttonLabel?: string;
  /** Button type like submit, button etc */
  @Input() buttonType?: 'button' | 'submit' | 'reset' | 'menu' = 'button';
  /** If button has been pressed */
  @Input() pressed?: boolean;
  /** Custom CSS for button text */
  @Input() buttonCustomCss?: string | string[] | { [key: string]: boolean };
  /** Button call on click */
  @Output() buttonClick = new EventEmitter<Event>();

  @HostBinding('class') get hostClasses() {
    return this.customHostClasses;
  }

  @HostBinding('attr.data-button-id') get buttonIdAttribute() {
    return this.buttonId;
  }

  onClick(event: Event) {
    if (this.disabled) {
      event.stopPropagation();
      return;
    }

    this.buttonClick.emit(event);
  }
}
